import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { DecadeRail } from "@/features/garimpos/decade-rail";
import { DECADE_NODE_TOP_CLASS, DecadeSection } from "@/features/garimpos/decade-section";
import { getCuratedCratesQueryOptions } from "@/features/garimpos/garimpos-query";
import { buildGarimposTimeline } from "@/features/garimpos/garimpos-timeline";
import { StyleIndex } from "@/features/garimpos/style-index";
import { useViewer } from "@/hooks/use-viewer";
import { cn } from "@/lib/utils";
import { getStoresQueryOptions } from "@/routes/_pages/$lang/stores/stores-query";

function GarimposHeader() {
  const t = useTranslations("garimpos");
  const storesQuery = useQuery(getStoresQueryOptions());
  const storeCount = storesQuery.data?.length;

  return (
    <header className="max-w-3xl">
      <h1 className="text-balance text-[clamp(2.25rem,6vw,3.75rem)] font-bold leading-[1.02] tracking-[-0.035em]">
        {t("title")}
      </h1>
      <p className="mt-4 max-w-[64ch] text-base leading-relaxed text-muted-foreground">
        {t("intro", {
          storeCountKnown: storeCount !== undefined && storeCount > 0 ? "yes" : "no",
          storeCount: storeCount ?? 0,
        })}
      </p>
    </header>
  );
}

function GarimposEmptyState() {
  const t = useTranslations("garimpos");

  return (
    <p className="mt-10 max-w-3xl rounded-2xl border border-dashed border-border px-6 py-12 text-center text-muted-foreground">
      {t.rich("emptyState", {
        link: (chunks) => (
          <Link
            to="/offers"
            className="font-semibold text-foreground underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {chunks}
          </Link>
        ),
      })}
    </p>
  );
}

/** The Garimpos hub: decades of curated crates on one spine, then every style A–Z. */
export function GarimposPage() {
  const viewer = useViewer();
  const cratesQuery = useQuery(
    getCuratedCratesQueryOptions({ currency: viewer.preferences.currency }),
  );
  const timeline = buildGarimposTimeline(cratesQuery.data?.crates ?? []);
  // SSR prefetches the crates; the empty state is for a failed or empty result, not a pending one.
  const showEmptyState = !timeline.length && !cratesQuery.isPending;

  return (
    <div className="px-4 pt-8 pb-20 sm:px-8 sm:pt-12 lg:px-12 lg:pb-24">
      <div className="mx-auto max-w-[1280px]">
        <GarimposHeader />

        {timeline.length ? (
          <div className="mt-8 md:mt-10 md:grid md:grid-cols-[5.5rem_minmax(0,1fr)] md:gap-x-6">
            <DecadeRail decades={timeline.map((decade) => decade.startYear)} />
            <div className={cn("relative mt-6 pl-6 sm:pl-8 md:mt-0", DECADE_NODE_TOP_CLASS)}>
              <span
                aria-hidden="true"
                className="absolute top-[calc(var(--decade-node-top)+0.4375rem)] bottom-0 left-0 w-0.5 rounded-full bg-primary"
              />
              {timeline.map((decade) => (
                <DecadeSection key={decade.startYear} decade={decade} />
              ))}
            </div>
          </div>
        ) : null}

        {showEmptyState ? <GarimposEmptyState /> : null}

        <div className="mt-4">
          <StyleIndex entries={cratesQuery.data?.styleIndex ?? []} />
        </div>
      </div>
    </div>
  );
}
